import type {
  UpdateWebsiteBannerDto,
  WebsiteBannerDto,
} from '@/generated/korza-api-client/types.gen';
import type { CouponBannerDto, WelcomeNotificationDto } from '@/lib/dtos';
import { bannerDtoToForm } from '@/lib/website-banner-constants';
import { isVersionGreaterOrEqual } from '@/lib/utils';

import {
  getDelayParts,
  isWelcomeDelay,
  type DelayParts,
} from './welcome-notification-form';

export type AppFeatureId =
  | 'couponBanner'
  | 'welcomeNotification'
  | 'websiteBanner'
  | 'abandonedCart';

export type AppFeatureState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'notConfigured' }
  | { kind: 'configured'; active: boolean }
  | { kind: 'waitingForUpdate' }
  | { kind: 'needsIntegration' }
  | { kind: 'comingSoon' };

export const COUPON_MIN_APP_VERSION = '1.21.0';

type QueryStatus = 'pending' | 'error' | 'success';

function getQueryFeatureState(
  status: QueryStatus,
  data: { active: boolean } | null | undefined
): AppFeatureState {
  if (status === 'pending') return { kind: 'loading' };
  if (status === 'error') return { kind: 'error' };
  if (!data) return { kind: 'notConfigured' };
  return { kind: 'configured', active: data.active };
}

export function getCouponFeatureState(input: {
  status: QueryStatus;
  data: CouponBannerDto | null | undefined;
  appVersion: string;
}): AppFeatureState {
  if (!isVersionGreaterOrEqual(input.appVersion, COUPON_MIN_APP_VERSION)) {
    return { kind: 'waitingForUpdate' };
  }
  return getQueryFeatureState(
    input.status,
    input.data && { active: input.data.isActive }
  );
}

export function getWelcomeFeatureState(input: {
  status: QueryStatus;
  data: WelcomeNotificationDto | null | undefined;
}): AppFeatureState {
  return getQueryFeatureState(
    input.status,
    input.data && { active: input.data.isActive }
  );
}

export function getWebsiteBannerFeatureState(input: {
  websiteBannerId: string | null;
  isStoreIntegrationConfigured: boolean;
  status: QueryStatus;
  data: WebsiteBannerDto | null | undefined;
}): AppFeatureState {
  if (!input.isStoreIntegrationConfigured) return { kind: 'needsIntegration' };
  if (input.websiteBannerId === null) return { kind: 'notConfigured' };
  return getQueryFeatureState(
    input.status,
    input.data && { active: input.data.enabled }
  );
}

export type FeatureSummary =
  | { kind: 'notConfigured' }
  | { kind: 'coupon'; couponCode: string; position: 'top' | 'bottom' }
  | { kind: 'welcome'; delay: DelayParts }
  | { kind: 'websiteBanner'; buttonText: string };

export function getFeatureSummary(
  id: Exclude<AppFeatureId, 'abandonedCart'>,
  data: CouponBannerDto | WelcomeNotificationDto | WebsiteBannerDto | null
): FeatureSummary {
  if (!data) return { kind: 'notConfigured' };
  if (id === 'couponBanner' && 'couponCode' in data) {
    return {
      kind: 'coupon',
      couponCode: data.couponCode,
      position: data.position,
    };
  }
  if (id === 'welcomeNotification' && 'delayMinutes' in data) {
    return {
      kind: 'welcome',
      delay: getDelayParts(
        isWelcomeDelay(data.delayMinutes) ? data.delayMinutes : 0
      ),
    };
  }
  if (id === 'websiteBanner' && 'content' in data) {
    return { kind: 'websiteBanner', buttonText: data.content.buttonText };
  }
  return { kind: 'notConfigured' };
}

export function toggledCouponDto(dto: CouponBannerDto): CouponBannerDto {
  return { ...dto, isActive: !dto.isActive };
}

export function toggledWelcomeDto(
  dto: WelcomeNotificationDto
): WelcomeNotificationDto {
  return { ...dto, isActive: !dto.isActive };
}

export function toggledWebsiteBannerBody(
  dto: WebsiteBannerDto
): UpdateWebsiteBannerDto {
  return { ...bannerDtoToForm(dto), enabled: !dto.enabled };
}

/** States whose card opens the editor (when the page gives `onOpen`). */
export function isFeatureOpenable(state: AppFeatureState): boolean {
  return (
    state.kind === 'configured' ||
    state.kind === 'notConfigured' ||
    state.kind === 'needsIntegration'
  );
}

/** Pill over a feature's thumbnail when it is not live in the app. */
export type FeatureGlimpseOverlay =
  | 'off'
  | 'notConfigured'
  | 'waitingForUpdate'
  | 'needsIntegration';

export function getFeatureGlimpseOverlay(
  state: AppFeatureState
): FeatureGlimpseOverlay | null {
  switch (state.kind) {
    case 'configured':
      return state.active ? null : 'off';
    case 'notConfigured':
    case 'waitingForUpdate':
    case 'needsIntegration':
      return state.kind;
    default:
      return null;
  }
}

/** The thumbnail goes grey when the feature is not live (or failed to load). */
export function isFeatureGlimpseDimmed(state: AppFeatureState): boolean {
  return getFeatureGlimpseOverlay(state) !== null || state.kind === 'error';
}
